import { AppChatRealtimeProvider } from '@/features/appChatRealtime/ui/AppChatRealtimeProvider';
import { useEffect } from 'react';
import { refreshAdminAccess } from '@/entities/AccessManagement/model/refreshAdminAccess';
import { useAdminAccess } from '@/shared/model/adminAccess';
import { AdminPermissionRoute } from '@/routes/AdminPermissionRoute';
import { ConfirmDialogWithCommentRoot } from './shared/ui/ConfirmDialogWithComment/ConfirmDialogWithComment';
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
    useLocation,
} from 'react-router-dom';
import { addLocale, locale, PrimeReactProvider } from 'primereact/api';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { ProtectedRoute } from './routes/ProtectedRoute'
import { routeConfig, RoutePath } from './core/config/routeConfig';
import { russianLocale } from './core/locales/ru';
import Layout from './shared/ui/Layout/Layout';
import AuthPage from './pages/Auth/AuthPage';
import { Toaster } from 'sonner';
import { ApiErrorDialog } from '@/shared/ui/ApiErrorDialog/ApiErrorDialog';
import { DesignStatesPage } from '@/pages/DesignStates/DesignStatesPage';
import { useAuth } from '@/entities/Auth/store';
import { useTheme } from '@/shared/model/theme';
import { UnsavedChangesGuard } from '@/shared/ui/UnsavedChangesGuard/UnsavedChangesGuard';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { discardUnsavedChanges, hasUnsavedChanges } from '@/shared/model/unsavedChanges';
import { OperatorTaskWorkspaceHeader } from '@/features/operatorTasks/ui/OperatorTaskWorkspaceHeader';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { ChatDialogRealtimeProvider } from '@/features/chatDialogRealtime/ui/ChatDialogRealtimeProvider';
import { ChatIncomingToaster } from '@/features/chatDialogRealtime/ui/ChatIncomingToaster';

addLocale('ru', russianLocale);
locale('ru');

const value = {
    locale: 'ru'
};

/** Открывает навигацию доступных разделов админки */
const AuthenticatedLayout = () => {
    const location = useLocation();
    const pathname = location.pathname.replace(/\/+$/, '');
    const isDialogsPage = pathname === RoutePath.chats || pathname === RoutePath.chatDialogs;
    const logout = useAuth((state) => state.logout);
    const roleCode = useAuth((state) => state.roleCode);
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const taskOverdueCount = useOperatorTasks((state) => state.summary?.overdueCount);

    useEffect(() => {
        void refreshAdminAccess();
        /** Обновляет доступные сотруднику действия */
        const refresh = () => void refreshAdminAccess();
        window.addEventListener('focus', refresh);

        return () => {
            window.removeEventListener('focus', refresh);
            useAdminAccess.getState().clear();
        };
    }, [roleCode, sessionRevision]);

    return (
        <>
            <UnsavedChangesGuard />
            <ChatDialogRealtimeProvider>
                <AppChatRealtimeProvider>
                    <Layout
                        logout={() => confirmUnsavedChanges(hasUnsavedChanges(), () => {
                            discardUnsavedChanges();
                            logout();
                        })}
                        roleCode={roleCode}
                        pageTitle={isDialogsPage
                            ? 'Диалоги'
                            : pathname === RoutePath.userChats
                                ? 'Переписки пользователей'
                                : undefined
                        }
                        fillViewport={isDialogsPage || pathname === RoutePath.orders}
                        workspaceHeader={<OperatorTaskWorkspaceHeader key={sessionRevision} />}
                        taskOverdueCount={taskOverdueCount}
                    />
                </AppChatRealtimeProvider>
            </ChatDialogRealtimeProvider>
        </>
    );
};

const router = createBrowserRouter([
    ...(import.meta.env.DEV || import.meta.env.MODE === 'e2e'
        ? [
            {
                path: '/design-states',
                element: <DesignStatesPage />,
            },
        ]
        : []),
    {
        path: '/auth',
        element: <AuthPage />,
    },
    {
        path: '/',
        element: (
            <ProtectedRoute>
                <AuthenticatedLayout />
            </ProtectedRoute>
        ),
        children: Object.values(routeConfig)
            .filter((config) => config.element)
            .map(({ path, element, allowedRoles, accessRoute }) => ({
                path,
                element: path && path !== '*'
                    ? (
                        <AdminPermissionRoute route={accessRoute ?? path}>
                            {allowedRoles
                                ? (
                                    <ProtectedRoute allowedRoles={allowedRoles}>
                                        {element}
                                    </ProtectedRoute>
                                )
                                : element}
                        </AdminPermissionRoute>
                    )
                    : element,
            })),
    },
    {
        path: '*',
        element: <Navigate
            to="/"
            replace
        />,
    },
]);

/** Открывает административное приложение */
function App() {
    const { resolved } = useTheme();

    return (
        <>
            <PrimeReactProvider value={value}>
                <RouterProvider router={router} />
                <Toaster theme={resolved} />
                <ChatIncomingToaster />
                <ApiErrorDialog />
                <ConfirmDialog />
                <ConfirmDialogWithCommentRoot />
            </PrimeReactProvider>
        </>
    );
}

export default App;
